import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { ETransportType } from '../../common/types';
import {
  HTTP_STATUS_CODES,
  LINK_DIRECTION_FROM,
  LINK_DIRECTION_TO,
  URL_LINKS,
} from '../../helpers/constants';
import { Sentry } from '../../libs/sentry';
import { getCompaniesData } from '../../services/data';
import request from '../../utils/request';
import {
  getLinksAction,
  setCityAction,
  setCompaniesData,
  setCurrentPageTypeAction,
  setEditableContentAction,
  setLinksAction,
  setLoadingAction,
  setRatingsAction,
  setShouldRedirectToTheMainPage,
  setTripDataAction,
} from './actions';
import {
  DESTINATION_CITY,
  FROM_DESTINATION_LINKS,
  FROM_ORIGIN_LINKS,
  GET_CITIES,
  GET_EDITABLE_CONTENT,
  GET_LINKS,
  ORIGIN_CITY,
  SEARCH_PAGE_TYPE_TO_TRANSPORT_TYPE,
  TO_DESTINATION_LINKS,
  TO_ORIGIN_LINKS,
  URL_CITIES,
  URL_EDITABLE_CONTENT,
  URL_RATINGS,
  URL_TRIP_DATA,
} from './constants';
import { doesAlternativePageExist, getMainSearchPageType } from './utils';

/**
 * @description Retrieves all data necessary for the Search page
 * @param {string} origin - Origin city slug
 * @param {string} destination - Destination city slug
 * @returns {void}
 */
export function* getSearch({ destination, origin, pageType }) {
  try {
    if (origin === destination) {
      return yield put(setLoadingAction(false));
    }

    const [originCity, destinationCity] = yield all([
      call(getCity, origin),
      call(getCity, destination),
    ]);

    yield put(setCityAction(ORIGIN_CITY, originCity));
    yield put(setCityAction(DESTINATION_CITY, destinationCity));

    if (!isCityValid(originCity) || !isCityValid(destinationCity)) {
      return yield put(setLoadingAction(false));
    }

    const tripData = yield call(getTripData, {
      originCityId: originCity.id,
      destinationCityId: destinationCity.id,
    });

    const shouldRedirectToMainPage =
      pageType &&
      !doesAlternativePageExist({
        tripData,
        pageType,
        originCity,
        destinationCity,
      });

    yield put(setShouldRedirectToTheMainPage(shouldRedirectToMainPage));

    if (shouldRedirectToMainPage) {
      return;
    }

    const mainPageType = getMainSearchPageType({
      destinationCity,
      originCity,
      tripData,
    });

    const currentPageType = pageType || mainPageType;
    const isMainPageType = currentPageType === mainPageType;
    const pageTransportType =
      SEARCH_PAGE_TYPE_TO_TRANSPORT_TYPE[currentPageType];

    yield put(setCurrentPageTypeAction(currentPageType));

    yield call(getEditableContent, {
      origin,
      destination,
      transportType: isMainPageType ? null : pageTransportType,
    });
    yield call(getRatings, {
      originId: originCity.id,
      destinationId: destinationCity.id,
    });

    const linkActions = isMainPageType
      ? [
          getLinksAction(LINK_DIRECTION_FROM, FROM_ORIGIN_LINKS, origin),
          getLinksAction(
            LINK_DIRECTION_FROM,
            FROM_DESTINATION_LINKS,
            destination,
          ),
          getLinksAction(LINK_DIRECTION_TO, TO_ORIGIN_LINKS, origin),
          getLinksAction(LINK_DIRECTION_TO, TO_DESTINATION_LINKS, destination),
        ]
      : [];

    yield all(linkActions.map((action) => put(action)));

    if (
      [ETransportType.BUS, ETransportType.TRAIN].includes(pageTransportType)
    ) {
      const companiesData = yield call(getCompaniesData, {
        originId: originCity.id,
        destinationId: destinationCity.id,
        transportType: pageTransportType,
      });

      yield put(
        setCompaniesData(isEmpty(companiesData) ? null : companiesData),
      );
    }

    yield put(setLoadingAction(false));
  } catch (error) {
    yield put(setLoadingAction(false));

    if (!error.capturedBySentry) {
      Sentry.captureException(error);
    }
  } finally {
    window.prerenderReady = true;
  }
}

/**
 * @description Checks if a city is valid
 * @param {{id: number,local_name: string, unique_name: string, new_id: string, serviced: boolean, not_serviced_since: string}} city
 * @returns {boolean}
 */
export function isCityValid(city) {
  return (
    city.id &&
    (city.serviced ||
      moment().diff(moment(city.not_serviced_since), 'month') < 12)
  );
}

/**
 * @description Retrieves city data from city unique name
 * @param {string} slug - City unique name
 * @return {{id: number,local_name: string, unique_name: string, new_id: string, serviced: boolean, not_serviced_since: string}}
 */
export function* getCity(slug) {
  try {
    return yield call(request, `${URL_CITIES}/${slug}`);
  } catch (error) {
    if (error.response?.status === HTTP_STATUS_CODES.NOT_FOUND) {
      return {};
    }

    throw error;
  }
}

/**
 * @description Retrieves trip data based of city ids
 * @param {number} originCityId - Origin city id
 * @param {number} destinationCityId - Destination city id
 * @returns {void}
 */
export function* getTripData({ destinationCityId, originCityId }) {
  const tripData = yield call(
    request,
    `${URL_TRIP_DATA}/${originCityId}/${destinationCityId}`,
  );

  const response = tripData.response;
  yield put(setTripDataAction(response));
  return response;
}

/**
 * @description Retrieves links from API based on a city slug and a wanted direction
 * @param {string} direction - Direction of the links requested (FROM | TO)
 * @param {string} linkType - What category of links (FROM_ORIGIN_LINKS | TO_ORIGIN_LINKS | FROM_DESTINATION_LINKS | TO_DESTINATION_LINKS)
 * @param {string} slug - City slug (unique name)
 * @param {number} [number] - Number of links requested
 * @returns {void}
 */
export function* getLinks({ direction, linkType, number = 12, slug }) {
  const links = yield call(
    request,
    `${URL_LINKS}/${direction}/${slug}/${number}`,
  );
  yield put(setLinksAction(direction, linkType, links));
}

/**
 * @description Retrieves editable content from API
 * @param {string} origin - Origin city slug
 * @param {string} destination - Destination city slug
 * @returns {void}
 */
export function* getEditableContent({ destination, origin, transportType }) {
  try {
    const extra = transportType ? `-${transportType}` : '';
    const contents = yield call(
      request,
      `${URL_EDITABLE_CONTENT}/search-${origin}-${destination}${extra}`,
    );
    yield put(setEditableContentAction(contents));
  } catch (error) {
    if (!error.capturedBySentry) {
      Sentry.captureException(error);
    }
  }
}

/**
 * @description Retrieves editable content from API
 * @param {number} originId - Origin city id
 * @param {number} destinationId - Destination city id
 * @returns {void}
 */
export function* getRatings({ destinationId, originId }) {
  const ratings = yield call(
    request,
    `${URL_RATINGS}/${originId}/${destinationId}`,
  );
  if (ratings && !ratings.error) {
    yield put(setRatingsAction(ratings));
  }
}

/**
 * @description Root saga
 * @returns {void}
 */
export default function* rootSaga() {
  yield all([
    takeLatest(GET_CITIES, getSearch),
    takeEvery(GET_LINKS, getLinks),
    takeLatest(GET_EDITABLE_CONTENT, getEditableContent),
  ]);
}
